import { Map } from "leaflet";
import { action, makeAutoObservable } from "mobx";
import {
    CircleZone,
    GeoZoneRecord,
    GeoZoneType,
    GetZoneBounds,
    Zone,
} from "../ui/features/Geozones/model";
import { RootStore } from "./Root.store";
import { geozonesAPI } from "../api/Geozones.api";

export default class GeoZoneStore {
    isPending: boolean = false;
    isSaving: boolean = false;
    isDeleting: boolean = false;

    map: Map | null = null;
    rootStore: RootStore;

    zones: GeoZoneRecord[];

    isEditing: boolean = false;
    editingZoneRecord: GeoZoneRecord | null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.editingZoneRecord = null;
        this.zones = [];
        makeAutoObservable(this, { rootStore: false }, { autoBind: true });
    }

    initMap(map: Map) {
        this.map = map;
    }

    subscribeOnEvents() {
        if (!this.map) return;
    }

    unsubscribeFromEvents() {
        if (!this.map) return;
    }

    fetchGeozones() {
        if (!this.rootStore.user.token) return;
        this.isPending = true;

        geozonesAPI
            .getAll(this.rootStore.user.token)
            .then(
                action((zones) => {
                    this.zones = zones;
                })
            )
            .catch(
                action((err) => {
                    console.error(err);
                })
            )
            .finally(
                action(() => {
                    this.isPending = false;
                })
            );
    }

    startNewGeoZone() {
        this.editingZoneRecord = null;
        this.isEditing = true;
    }

    startEditGeoZone(id: GeoZoneRecord["id"]) {
        const zone = this.zones.find((z) => z.id === id);
        if (!zone) return;

        this.editingZoneRecord = zone;
        this.isEditing = true;
    }

    deleteGeoZone(id: GeoZoneRecord["id"]) {
        if (!this.rootStore.user.token) return;
        this.isDeleting = true;

        geozonesAPI
            .delete(this.rootStore.user.token, id)
            .then(
                action((zones) => {
                    this.zones = this.zones.filter((z) => z.id !== id);
                })
            )
            .catch(
                action((err) => {
                    console.error(err);
                })
            )
            .finally(
                action(() => {
                    this.isDeleting = false;
                })
            );
    }

    saveEditGeoZone(name: string, zone: Zone) {
        if (!this.rootStore.user.token) return;
        const isNewGeozone = this.editingZoneRecord === null;

        this.isSaving = true;
        if (isNewGeozone) {
            geozonesAPI
                .create(this.rootStore.user.token, { name, zone })
                .then(
                    action(({ id }) => {
                        this.zones = this.zones.concat({
                            id,
                            name,
                            zone,
                        });
                    })
                )
                .catch(
                    action((err) => {
                        console.error(err);
                    })
                )
                .finally(
                    action(() => {
                        this.isSaving = false;
                        this.stopEdit();
                    })
                );
        } else {
            geozonesAPI
                .update(this.rootStore.user.token, { 
                    id: this.editingZoneRecord!.id,
                    name, 
                    zone })
                .then(
                    action(() => {
                        this.editingZoneRecord!.name = name;
                        this.editingZoneRecord!.zone = zone;
                    })
                )
                .catch(
                    action((err) => {
                        console.error(err);
                    })
                )
                .finally(
                    action(() => {
                        this.isSaving = false;
                        this.stopEdit();
                    })
                );
        }
    }

    stopEdit() {
        this.isEditing = false;
        this.editingZoneRecord = null;
    }

    focusOnZone(zone: Zone) {
        if (!this.map) return;
        const bounds = GetZoneBounds(zone);
        console.log(bounds);
        this.map.fitBounds(bounds);
    }
}
